import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { startGame, submitAnswer, endGameEarly, GameSession, Card, GameSummary } from '../api/game';
import { useOnboarding } from '../context/OnboardingContext';
import Timer from '../components/Timer';
import AnswerButton from '../components/AnswerButton';
import OnboardingTooltip from '../components/OnboardingTooltip';
import '../styles/global.css';

type AnswerState = 'default' | 'correct' | 'wrong' | 'missed';

const Game: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const { shouldShowTooltip, dismissStep, isOnboardingActive } = useOnboarding();

  // Onboarding refs
  const timerAreaRef = useRef<HTMLDivElement>(null);
  const answersAreaRef = useRef<HTMLDivElement>(null);

  const [session, setSession] = useState<GameSession | null>(null);
  const [currentCard, setCurrentCard] = useState<Card | null>(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [maxTime, setMaxTime] = useState(5);
  const [score, setScore] = useState({ xp: 0, coins: 0 });
  const [currentCardNum, setCurrentCardNum] = useState(1);
  const [totalCards, setTotalCards] = useState(20);
  const [correctCount, setCorrectCount] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showingResult, setShowingResult] = useState(false);
  const [lastCorrectAnswer, setLastCorrectAnswer] = useState('');
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [wasCorrect, setWasCorrect] = useState(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const sessionIdRef = useRef<string | null>(null);

  // Start game on mount
  useEffect(() => {
    const initGame = async () => {
      if (!categoryId) {
        setError('Invalid category');
        setLoading(false);
        return;
      }

      try {
        const gameSession = await startGame(categoryId);
        setSession(gameSession);
        sessionIdRef.current = gameSession.session_id;
        setCurrentCard(gameSession.card);
        setMaxTime(gameSession.category.time_limit_seconds);
        setTimeLeft(gameSession.category.time_limit_seconds);
        setTotalCards(gameSession.total_cards);
        setCurrentCardNum(gameSession.current_card);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to start game');
      } finally {
        setLoading(false);
      }
    };

    initGame();

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [categoryId]);

  // Whether game onboarding tooltips are currently showing
  const gameOnboardingVisible = isOnboardingActive('game');

  // Timer countdown (frozen while onboarding tooltips are visible)
  useEffect(() => {
    if (loading || showingResult || !currentCard || gameOnboardingVisible) return;

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          // Time's up - auto-submit as wrong
          handleTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [loading, showingResult, currentCard, gameOnboardingVisible]);

  const handleTimeout = useCallback(async () => {
    if (!sessionIdRef.current || showingResult) return;

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    setShowingResult(true);
    setSelectedAnswer(null);
    setWasCorrect(false);

    try {
      const result = await submitAnswer(sessionIdRef.current, '');
      setLastCorrectAnswer(result.correct_answer);

      // Wait 1.5 seconds to show the correct answer
      setTimeout(() => {
        if (result.session_complete && result.summary) {
          navigateToResults(result.summary);
        } else if (result.next_card) {
          setCurrentCard(result.next_card);
          setCurrentCardNum(result.current_card);
          setTimeLeft(maxTime);
          setShowingResult(false);
          setSelectedAnswer(null);
        }
      }, 1500);
    } catch (err) {
      setError('Failed to submit answer');
      setShowingResult(false);
    }
  }, [maxTime, showingResult]);

  const handleAnswer = async (answer: string) => {
    if (!sessionIdRef.current || showingResult) return;

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    setShowingResult(true);
    setSelectedAnswer(answer);

    try {
      const result = await submitAnswer(sessionIdRef.current, answer);
      setWasCorrect(result.correct);
      setLastCorrectAnswer(result.correct_answer);

      if (result.correct) {
        setCorrectCount((prev) => prev + 1);
        setScore((prev) => ({
          xp: prev.xp + result.xp_earned,
          coins: prev.coins + result.coins_earned,
        }));
      }

      // Wait 1 second to show result
      setTimeout(() => {
        if (result.session_complete && result.summary) {
          navigateToResults(result.summary);
        } else if (result.next_card) {
          setCurrentCard(result.next_card);
          setCurrentCardNum(result.current_card);
          setTimeLeft(maxTime);
          setShowingResult(false);
          setSelectedAnswer(null);
        }
      }, 1000);
    } catch (err) {
      setError('Failed to submit answer');
      setShowingResult(false);
    }
  };

  const handleEndEarly = async () => {
    if (!sessionIdRef.current) return;

    try {
      const result = await endGameEarly(sessionIdRef.current);
      navigateToResults(result.summary);
    } catch (err) {
      setError('Failed to end game');
    }
  };

  const handleAbandon = () => {
    // Abandon game without saving - no points/XP awarded
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    navigate('/account');
  };

  const navigateToResults = (summary: GameSummary) => {
    navigate('/game/results', { state: { summary, categoryId } });
  };

  const getAnswerState = (answer: string): AnswerState => {
    if (!showingResult) return 'default';
    if (answer === lastCorrectAnswer) return 'correct';
    if (answer === selectedAnswer && !wasCorrect) return 'wrong';
    if (selectedAnswer === null && answer !== lastCorrectAnswer) return 'missed';
    return 'default';
  };

  if (loading) {
    return (
      <div className="page">
        <div className="game-loading">
          <p>Starting game...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="container">
          <div className="error">{error}</div>
          <button className="btn btn-primary" onClick={() => navigate('/account')} style={{ marginTop: 20 }}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (!currentCard || !session) {
    return (
      <div className="page">
        <div className="container">
          <div className="error">No card available</div>
        </div>
      </div>
    );
  }

  const labels = ['A', 'B', 'C', 'D'];

  return (
    <div className="page game-page">
      <div className="game-header">
        <div className="game-progress">
          Question {currentCardNum} / {totalCards}
        </div>
        <div className="game-correct-count">
          {correctCount} correct of {currentCardNum - 1} answered
        </div>
        <button className="game-end-btn" onClick={handleAbandon}>
          End
        </button>
      </div>

      <div className="game-container">
        <div className="game-category">{session.category.name}</div>

        <div ref={timerAreaRef}>
          <Timer timeLeft={timeLeft} maxTime={maxTime} />
        </div>

        <div className="question-card">
          <div className="question-text">{currentCard.question}</div>
        </div>

        <div ref={answersAreaRef} className="answers-grid">
          {currentCard.options.map((option, index) => (
            <AnswerButton
              key={index}
              label={labels[index]}
              answer={option}
              onClick={handleAnswer}
              disabled={showingResult || gameOnboardingVisible}
              state={getAnswerState(option)}
            />
          ))}
        </div>
      </div>

      {/* Onboarding tooltips */}
      {shouldShowTooltip('game', 1) && (
        <OnboardingTooltip
          targetRef={timerAreaRef}
          title="Watch the clock!"
          message="Answer before time runs out to earn more XP."
          onDismiss={() => dismissStep('game', 1)}
        />
      )}
      {shouldShowTooltip('game', 2) && (
        <OnboardingTooltip
          targetRef={answersAreaRef}
          title="Tap the right answer!"
          message="Green means correct, red means wrong. Let's go!"
          onDismiss={() => dismissStep('game', 2)}
        />
      )}
    </div>
  );
};

export default Game;
